import { SidebarRequestLogCard } from "@/components/SidebarRequestLogCard";
import { SIDEBAR_MAX } from "@/constants";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { hydrateSidebarSignalBubbles } from "@/routes/request-logs/search/signal-search-utils";
import { useWorkspaceSignalNames } from "@/routes/request-logs/search/useWorkspaceSignalNames";
import type { RequestPreview } from "@/types/requests";
import {
  REQUEST_LOG_INPUT_PREVIEW_LABEL,
  formatRequestLogPreviewDisplay,
  getRequestLogInputPreviewText,
} from "@/utils/requestLogPreviewDisplay";
import { responseIsFunctionOrTool } from "@/utils/logUtils";

interface RequestLogCardsProps {
  requests: RequestPreview[];
  requestId?: string;
  groupId?: string;
  recentlyViewedRequestId: string | null;
  onViewedRequest: (id: number) => void;
}

const RequestLogCards = ({
  requests,
  requestId,
  groupId,
  recentlyViewedRequestId,
  onViewedRequest,
}: RequestLogCardsProps) => {
  const userToken = useUserToken();
  const { activeWorkspaceId } = useUser();
  const signalNamesById = useWorkspaceSignalNames(userToken, activeWorkspaceId);

  return (
    <>
      {requests.slice(0, SIDEBAR_MAX).map(item => {
        const active =
          !!(requestId && String(item.id) === requestId) ||
          !!(groupId && String(item.id) === groupId);
        const recentlyViewed =
          item.type !== "group" && !active && recentlyViewedRequestId === String(item.id);

        const inputPreview = formatRequestLogPreviewDisplay(getRequestLogInputPreviewText(item));
        const responsePreview = formatRequestLogPreviewDisplay(item.request_response_preview, {
          highlightAnnotations: item.request_response_preview_highlight_annotations,
        });
        const signals = hydrateSidebarSignalBubbles(item.metadata, signalNamesById);

        return (
          <SidebarRequestLogCard
            // TODO (naveed) LOW: Index-based keys cause React to throw away and remount components whenever the array shifts (infinite scroll load, inserts, etc.).
            // and whenever the array changes React consider it as different component and re-renders it. we should use stable ids instead.
            // https://react.dev/learn/rendering-lists#why-does-react-need-keys
            key={item.id}
            active={active}
            logId={item.id}
            descriptor={item.engine || item.function_name}
            startTime={item.request_start_time}
            tags={item.tags}
            signals={signals}
            starred={item.is_starred}
            prompt={inputPreview.display}
            promptTitle={inputPreview.title}
            promptLabel={REQUEST_LOG_INPUT_PREVIEW_LABEL}
            promptTextSearchMatch={Boolean(item.prompt_text_search_match)}
            responseIsFunction={responseIsFunctionOrTool(item)}
            response={responsePreview.display}
            responseTitle={responsePreview.title}
            type={item.type}
            cardinality={item.cardinality}
            promptName={item.prompt_name}
            promptId={item.prompt_id}
            promptVersionNumber={item.prompt_version_number}
            provider={item.provider_type}
            providerId={item.provider_id}
            status={item.status}
            errorType={item.error_type}
            errorMessage={item.error_message}
            recentlyViewed={recentlyViewed}
            onViewed={onViewedRequest}
          />
        );
      })}
    </>
  );
};

export default RequestLogCards;
